import AuthProvider from "./auth/AuthProvider/Index";
import { mergeErrors } from "../utils"; // Assuming this utility exists

// Define a custom error class to hold status and body
export class HttpError extends Error {
    status: number;
    body: any; // Can hold the parsed JSON error body

    constructor(message: string, status: number, body: any = null) {
        super(message); // Pass message to the Error base class
        this.name = 'HttpError'; // Useful for identification
        this.status = status;
        this.body = body;
         // Maintains proper stack trace in V8 environments (optional but good)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, HttpError);
        }
    }
}

export const fetchResults = async (serialNumber: string): Promise<any> => { // Added Promise<any> return type hint
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/results`;

    try {
        const token = await AuthProvider.getValidAuthorizationToken(); // Ensure token is awaited
        const response = await fetch(apiUrl, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`, // Use the retrieved token
            },
            method: "GET",
        });

        // --- Success Case ---
        if (response.ok) {
            const result = await response.json();
            // Assuming the backend nests the actual data under 'data' based on your previous component
            if (!result?.data) {
                 console.warn("API response OK, but 'data' field is missing or undefined.", result);
                 // Throw an error or return null/empty object based on expected handling
                 throw new Error("Received unexpected data structure from API.");
            }
            return result.data;
        }

        // --- Error Case (!response.ok) ---

        let errorBody: any = null;
        let errorMessage = `Request failed with status ${response.status}`; // Default message

        // Try to parse the response body as JSON, it might contain useful error details
        try {
            errorBody = await response.json();
            // Use message from backend response if available
            if (errorBody && typeof errorBody.message === 'string') {
                errorMessage = errorBody.message;
            } else if (errorBody && typeof errorBody.error === 'string') {
                 // Fallback check for 'error' field if 'message' isn't there
                errorMessage = errorBody.error;
            }
        } catch (jsonError) {
            // If response body is not JSON or empty, log warning but proceed
            console.warn(`Could not parse error response body for status ${response.status}:`, jsonError);
            // Use status text as a fallback message if parsing failed
            errorMessage = `Request failed with status ${response.status}: ${response.statusText}`;
        }

        // Specific handling for 422 Validation Errors, using parsed body if available
        if (response.status === 422 && errorBody?.errors) {
            errorMessage = `Validation error: ${JSON.stringify(mergeErrors(errorBody.errors))}`;
             // Throw the custom HttpError, including status and body
            throw new HttpError(errorMessage, response.status, errorBody);
        }

        // For ALL non-ok responses (including 403, 404, 500, etc.)
        // Throw the custom HttpError, including status and body
        throw new HttpError(errorMessage, response.status, errorBody);

    } catch (error) {
        // Log the error regardless of its type (Network error, HttpError, etc.)
        console.error("Error during fetchResults execution:", error);

        // If it's already our custom HttpError or a generic Error, re-throw it
        // If it's something else, wrap it or handle appropriately if needed
        // For simplicity, we just re-throw here.
        throw error;
    }

    // This line is unreachable if success returns and errors throw, which is good.
    // It's kept here only if you modify logic to potentially not throw on certain errors.
    // return null;
};